import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, User, Zap, Accessibility, Smartphone } from "lucide-react";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { Link } from "react-router-dom";
import aiBrainHero from "@/assets/blog/ai-abstract-dark.jpg";
const BlogPostMaster = () => {
  const relatedArticles = [
    { title: "The 0.5-Second Rule", tag: "Speed", icon: Zap },
    { title: "The Hidden Market", tag: "Accessibility", icon: Accessibility },
    { title: "Stop Losing Leads on Mobile", tag: "Mobile", icon: Smartphone },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>Why SEO is Dead | Insights for South Wales Trades</title>
        <meta name="description" content="Expert insights on AI search optimisation and ranking in 2026. Learn how to make your trade website AI-ready for ChatGPT and voice assistants." />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/blog-post-master" />
      </Helmet>
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Insights", href: "/insights" },
            { label: "Why SEO is Dead", href: "/blog-post-master" }
          ]} />
          
          {/* Section 1: Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0 px-4 py-1.5 text-sm font-medium rounded-full mb-6">
              2026 Insights
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Why SEO is Dead: How to Rank in the Age of{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                AI
              </span>
            </h1>
            
            <div className="flex items-center justify-center gap-4 text-slate-400">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>By SmartSite Team</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>4 min read</span>
              </div>
            </div>
          </motion.header>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 relative">
              <img 
                src={aiBrainHero}
                alt="Artificial intelligence neural network visualisation representing modern AI-powered SEO"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1024px"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Dark overlay for brand consistency */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
              {/* Accent glow overlays */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(251,146,60,0.15),transparent_50%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(34,211,238,0.1),transparent_50%)]" />
            </div>
          </motion.div>

          {/* Section 2: Two Column Layout */}
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_380px] gap-8">
              
              {/* Left Column - Article Content (70%) */}
              <motion.article
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="p-8 md:p-12">
                    {/* Intro */}
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 font-light">
                      The old way of SEO, stuffing keywords and buying links, is over. Tools like ChatGPT and Gemini don't "read" websites like a human; they "parse" data like a machine. If your site isn't speaking their language, you are invisible.
                    </p>

                    {/* Subhead 1 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      The Shift from Keywords to{" "}
                      <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                        Context
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      AI doesn't look for "Plumber Cardiff." It looks for entities: [Service: Plumbing] + [Location: Cardiff] + [Trust: High]. This requires JSON-LD Schema Markup, not just text on a page.
                    </p>

                    {/* Subhead 2 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      Why Speed is the New{" "}
                      <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
                        Trust Factor
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      AI tools prioritise user experience. If your site takes 3 seconds to load, the AI assumes the experience is poor and recommends a competitor. We build on React to ensure 0.5s load times.
                    </p>

                    {/* Conclusion */}
                    <div className="border-l-4 border-orange-500 pl-6 py-2">
                      <p className="text-lg text-white font-medium italic">
                        To rank in 2026, you need to stop building for Google 2015 and start building for Intelligence 2026.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.article>

              {/* Right Column - Sticky Sidebar (30%) */}
              <motion.aside
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-6"
              >
                <div className="lg:sticky lg:top-28 space-y-6">
                  {/* Author Bio Card */}
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
                          SS
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">SmartSite Solutions</h3>
                          <p className="text-slate-400 text-sm">North Cornelly</p>
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        Engineering growth for South Wales trades.
                      </p>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 backdrop-blur-xl border-orange-500/20 rounded-2xl overflow-hidden">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-white mb-3">
                        Is your site AI-Ready?
                      </h3>
                      <p className="text-slate-400 text-sm mb-6">
                        Get a free audit and see how your site performs for AI search engines.
                      </p>
                      <PrimaryCTA 
                        text="Get Free AI Audit" 
                        href="/contact" 
                        external={false}
                        fullWidth
                      />
                    </CardContent>
                  </Card>

                  {/* Services Links Card */}
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <h3 className="text-white font-semibold mb-4">Our Services</h3>
                      <ul className="space-y-3">
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 group-hover:scale-125 transition-transform" />
                            Web Design
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 group-hover:scale-125 transition-transform" />
                            Local SEO
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover:scale-125 transition-transform" />
                            Care Plans
                          </Link>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </motion.aside>
            </div>
          </div>

          {/* Section 3: Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="max-w-6xl mx-auto mt-20"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              More{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Insights
              </span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <motion.div
                  key={article.tag}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                >
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 group cursor-pointer h-full">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-white/10">
                        <article.icon className="w-6 h-6 text-orange-400" />
                      </div>
                      <Badge variant="outline" className="border-white/20 text-slate-400 mb-3">
                        {article.tag}
                      </Badge>
                      <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors">
                        {article.title}
                      </h3>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostMaster;
